/**
 * CartPageViewModel - Cart Page Orchestration
 * 
 * Manages complete cart page functionality for PageContentCart component
 * Handles cart items, address integration, quantity management, and checkout navigation
 * 
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useMemo } from 'react'
import type { IPlatformServices } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'


// Types for cart page data structures
export interface SectionCartItem {
  id: string
  productName: string
  priceCurrent: string
  priceOld?: string
  showPriceOld?: boolean
  productOptions?: string
  showProductOptions?: boolean
  quantity: number
  maxQuantity?: number
}

export interface AddressSummary {
  street: string
  cityStateZip: string
  estimatedDelivery?: string
}

// Service interfaces for dependency injection
export interface CartService {
  getCartItems: () => Promise<any[]>
  updateQuantity: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  getSubtotal: () => Promise<number>
  clearCart: () => Promise<void>
  getFreeItemIds?: () => string[]
  getOfferDiscountValue?: () => number
}

export interface AddressService {
  getDeliveryAddress: () => Promise<AddressSummary | null>
  updateDeliveryAddress: (address: AddressSummary) => Promise<void>
}

export interface CheckoutService {
  proceedToCheckout: () => Promise<{ success: boolean }>
  validateCartForCheckout: () => Promise<{ isValid: boolean; errors: string[] }>
}

export interface CartPageViewModelDependencies {
  cartService?: CartService
  addressService?: AddressService
  checkoutService?: CheckoutService
}

// ViewModel state interface
export interface CartPageViewModelState {
  // Cart data
  cartItems: SectionCartItem[]
  address: AddressSummary | null | undefined
  subtotal: string
  continueButtonText: string
  
  // Page states
  loading: boolean
  error: string | null
  disabled: boolean
  
  // Computed properties
  isCartEmpty: boolean
  canProceedToCheckout: boolean
}

// ViewModel actions interface
export interface CartPageViewModelActions {
  // Address management
  onEditAddress: () => Promise<void>
  
  // Cart item management
  onEditCartItem: (itemId: string) => Promise<void>
  onIncreaseQuantity: (itemId: string) => Promise<void>
  onDecreaseQuantity: (itemId: string) => Promise<void>
  onQuantityChange: (itemId: string, quantity: number) => Promise<void>
  
  // Checkout
  onContinue: () => Promise<void>
  
  // Error handling
  clearError: () => void
  refresh: () => Promise<void>
}

// Combined ViewModel interface
export interface CartPageViewModel extends CartPageViewModelState, CartPageViewModelActions {}

/**
 * Platform-agnostic CartPageViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createCartPageViewModel = (
  services: IPlatformServices,
  dependencies?: CartPageViewModelDependencies
) => {
  return (): CartPageViewModel => {
    // State management
    const [cartItems, setCartItems] = useState<SectionCartItem[]>([])
    const [address, setAddress] = useState<AddressSummary | null | undefined>(undefined)
    const [subtotal, setSubtotal] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    // `disabled` is currently always false; no setter is wired up yet.
    const [disabled] = useState<boolean>(false)

    // Service access
    const { cartService, addressService, checkoutService } = dependencies || {}

    // Helper function to format price
    const formatPrice = useCallback((amount: number): string => {
      return `$${amount.toFixed(2)}`
    }, [])

    // Transform cart data from service to component format
    const transformCartItemsForComponent = useCallback((
      items: any[],
      freeItemIds?: string[],
      offerDiscountPct?: number
    ): SectionCartItem[] => {
      const freeSet = new Set(freeItemIds || [])
      return items.map(item => {
        const unitPrice = item.totalPrice / item.quantity || 0
        const isFreeItem = freeSet.has(item.id)
        const discountPct = isFreeItem ? (offerDiscountPct ?? 100) : 0
        const isFullyFree = isFreeItem && discountPct >= 100
        const discountedPrice = isFreeItem ? unitPrice * (1 - discountPct / 100) : unitPrice

        // Determine price display: existing originalPrice OR offer free-item pricing
        let priceOld = item.menuItem?.originalPrice ? formatPrice(item.menuItem.originalPrice) : undefined
        let showPriceOld = Boolean(item.menuItem?.originalPrice && item.menuItem.originalPrice > unitPrice)
        let priceCurrent = formatPrice(unitPrice)

        if (isFreeItem) {
          priceOld = formatPrice(unitPrice)
          showPriceOld = true
          priceCurrent = isFullyFree ? 'FREE' : formatPrice(discountedPrice)
        }

        return {
          id: item.id,
          productName: item.menuItem?.name || item.productName || 'Unknown Product',
          priceCurrent,
          priceOld,
          showPriceOld,
          productOptions: item.customizations?.length > 0
            ? item.customizations.map((c: any) => {
                const group = item.menuItem?.customizations?.find(
                  (cust: any) => cust.id === c.customizationItemId
                )
                const option = group?.options?.find(
                  (opt: any) => opt.id === c.selectedOptionId
                )
                return option?.name
              }).filter(Boolean).join('; ')
            : item.productOptions,
          showProductOptions: Boolean(item.customizations?.length > 0 || item.productOptions),
          quantity: item.quantity || 1,
          ...(isFreeItem ? { maxQuantity: item.quantity || 1 } : {}),
        }
      })
    }, [formatPrice])

    // Load cart data
    const loadCartData = useCallback(async () => {
      if (!cartService) {
        setLoading(false)
        return
      }

      try {
        setError(null)
        const [items, subtotalAmount] = await Promise.all([
          cartService.getCartItems(),
          cartService.getSubtotal()
        ])
        
        const freeIds = cartService.getFreeItemIds?.() || []
        const discountVal = cartService.getOfferDiscountValue?.() || 100
        setCartItems(transformCartItemsForComponent(items, freeIds, discountVal))
        setSubtotal(formatPrice(subtotalAmount))
      } catch (err) {
        safeGetLogger()?.error('Failed to load cart data', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to load cart')
      }
    }, [cartService, transformCartItemsForComponent, formatPrice])

    // Load address data
    const loadAddressData = useCallback(async () => {
      if (!addressService) {
        setAddress(null)
        return
      }

      try {
        const deliveryAddress = await addressService.getDeliveryAddress()
        setAddress(deliveryAddress)
      } catch (err) {
        safeGetLogger()?.error('Failed to load address data', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to load address')
      }
    }, [addressService])

    // Load all page data
    const loadPageData = useCallback(async () => {
      setLoading(true)
      setError(null)

      try {
        await Promise.all([
          loadCartData(),
          loadAddressData()
        ])
      } catch (err) {
        safeGetLogger()?.error('Failed to load page data', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to load page data')
      } finally {
        setLoading(false)
      }
    }, [loadCartData, loadAddressData])

    // Load data on component mount
    useEffect(() => {
      loadPageData()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Computed properties
    const isCartEmpty = useMemo(() => {
      return cartItems.length === 0
    }, [cartItems])

    const canProceedToCheckout = useMemo(() => {
      return !isCartEmpty && !loading && !error
    }, [isCartEmpty, loading, error])

    // Address management
    const onEditAddress = useCallback(async () => {
      try {
        await services.navigationService.navigate('/address/edit')
      } catch (err) {
        safeGetLogger()?.error('Failed to navigate to address edit', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Navigation failed')
      }
    }, [services.navigationService])

    // Cart item editing
    const onEditCartItem = useCallback(async (itemId: string) => {
      try {
        await services.navigationService.navigate(`/product/edit/${itemId}`)
      } catch (err) {
        safeGetLogger()?.error('Failed to navigate to item edit', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Navigation failed')
      }
    }, [services.navigationService])

    // Quantity management helpers
    const updateCartAfterChange = useCallback(async () => {
      try {
        await loadCartData()

        // Navigate back to menu if cart is empty
        const items = await cartService?.getCartItems()
        if (items && items.length === 0) {
          safeGetLogger()?.cart('cleared', { reason: 'navigating_to_menu' })
          await services.navigationService.navigate('/menu')
        }
      } catch (err) {
        safeGetLogger()?.error('Failed to refresh cart after update', { domain: 'cart', error: String(err) })
      }
    }, [loadCartData, cartService, services.navigationService])

    // Increase quantity
    const onIncreaseQuantity = useCallback(async (itemId: string) => {
      if (!cartService) return

      try {
        setError(null)
        setLoading(true)

        // Find current quantity from current state
        const currentItem = cartItems.find(item => item.id === itemId)
        const newQuantity = (currentItem?.quantity || 0) + 1

        await cartService.updateQuantity(itemId, newQuantity)
        await updateCartAfterChange()
      } catch (err) {
        safeGetLogger()?.error('Failed to increase quantity', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to update quantity')
      } finally {
        setLoading(false)
      }
    }, [cartService, cartItems, updateCartAfterChange])

    // Decrease quantity
    const onDecreaseQuantity = useCallback(async (itemId: string) => {
      if (!cartService) return

      try {
        setError(null)
        setLoading(true)

        // Find current quantity from current state
        const currentItem = cartItems.find(item => item.id === itemId)
        const currentQuantity = currentItem?.quantity || 0

        if (currentQuantity <= 1) {
          // Remove item if quantity would become 0
          await cartService.removeItem(itemId)
        } else {
          // Decrease quantity
          await cartService.updateQuantity(itemId, currentQuantity - 1)
        }

        await updateCartAfterChange()
      } catch (err) {
        safeGetLogger()?.error('Failed to decrease quantity', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to update quantity')
      } finally {
        setLoading(false)
      }
    }, [cartService, cartItems, updateCartAfterChange])

    // Direct quantity change
    const onQuantityChange = useCallback(async (itemId: string, quantity: number) => {
      if (!cartService) return

      try {
        setError(null)

        if (quantity <= 0) {
          await cartService.removeItem(itemId)
        } else {
          await cartService.updateQuantity(itemId, quantity)
        }

        await updateCartAfterChange()
      } catch (err) {
        safeGetLogger()?.error('Failed to update quantity', { domain: 'cart', error: String(err) })
        setError(err instanceof Error ? err.message : 'Failed to update quantity')
      }
    }, [cartService, updateCartAfterChange])

    // Checkout process
    const onContinue = useCallback(async () => {
      if (!checkoutService) {
        setError('Checkout service not available')
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Check if cart is empty
        if (isCartEmpty) {
          setError('Your cart is empty')
          return
        }

        // Validate cart for checkout
        const validation = await checkoutService.validateCartForCheckout()
        if (!validation.isValid) {
          setError(validation.errors[0] || 'Cart is not valid for checkout')
          return
        }

        // Proceed to checkout
        const result = await checkoutService.proceedToCheckout()
        if (result.success) {
          await services.navigationService.navigate('/checkout')
        } else {
          setError('Failed to proceed to checkout')
        }
      } catch (err) {
        safeGetLogger()?.error('Checkout initiation failed', { domain: 'checkout', error: String(err) })
        setError(err instanceof Error ? err.message : 'Checkout failed')
      } finally {
        setLoading(false)
      }
    }, [checkoutService, isCartEmpty, services.navigationService])

    // Error handling
    const clearError = useCallback(() => {
      setError(null)
    }, [])

    // Refresh page data
    const refresh = useCallback(async () => {
      await loadPageData()
    }, [loadPageData])

    return {
      // State
      cartItems,
      address,
      subtotal,
      continueButtonText: 'Continue',
      loading,
      error,
      disabled,
      isCartEmpty,
      canProceedToCheckout,

      // Actions
      onEditAddress,
      onEditCartItem,
      onIncreaseQuantity,
      onDecreaseQuantity,
      onQuantityChange,
      onContinue,
      clearError,
      refresh
    }
  }
}